import React from "react";
import { Box, Typography, IconButton, Divider, Chip, Button } from "@mui/material";
import { useTranslation } from "react-i18next";
import ClearIcon from "@mui/icons-material/Clear";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import RefreshIcon from "@mui/icons-material/Refresh";
import UITooltip from "components/ui/Tooltip/UITooltip";
import ProgressBar from "components/loader/LinearProgress";
import dayjs from "dayjs";
import { TASK_STATUS } from "config/constants/constants";
import { useSelector } from "react-redux";
import { LANGUAGE_CODE_MAP } from "config/helpers/prompts";
import { getCurrentProgressState } from "config/helpers/progressStateSelector";
import { getCurrentRecordingState } from "config/helpers/recordingStateSelector";

const formatNotificationTimestamp = (timestamp) => {
  const now = dayjs();
  const notificationDate = dayjs(timestamp);

  if (notificationDate.isSame(now, "day")) {
    const diffMins = now.diff(notificationDate, "minute");
    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHours = now.diff(notificationDate, "hour");
    return `${diffHours}h ago`;
  }
  return notificationDate.format("MMM D");
};

const getStatusProps = (status, isDarkTheme) => {
  switch (status) {
    case TASK_STATUS.COMPLETED:
      return {
        icon: <CheckCircleIcon sx={{ color: "#4CAF50", fontSize: "20px" }} />,
        color: "#48BA7814",
        hoverColor: "#48BA7824",
        borderColor: "#48BA78",
      };
    case TASK_STATUS.FAILED:
      return {
        icon: <ErrorIcon sx={{ color: "#f44336", fontSize: "20px" }} />,
        color: isDarkTheme ? "#DE302414" : "#DE30240F",
        hoverColor: isDarkTheme ? "#DE302424" : "#DE30241F",
        borderColor: "#DE3024",
      };
    case TASK_STATUS.IN_PROGRESS:
    case TASK_STATUS.QUEUED:
    case TASK_STATUS.RUNNING:
    case TASK_STATUS.PENDING:
      return {
        icon: null,
        color: "#5D5FEF14",
        hoverColor: "#5D5FEF14",
        borderColor: "#5D5FEF",
      };
    default:
      return {
        icon: null,
        color: isDarkTheme ? "#333" : "#f0f0f0",
        borderColor: "grey.500",
        hoverColor: isDarkTheme ? "#444" : "#e0e0e0",
      };
  }
};

const BackgroundTaskFooter = ({ notification, isDarkTheme, i18n, onRetry, isDisabled, isClickable, onClick, isUpdating, isInProgress, isEncounterArchived , isRecording , isPaused,isProgressReady }) => {
  const soapNoteTask = notification?.taskGroups?.find(
    (item) => item.code === "task_group@notes.soapnote.generate"
  );
  const isNonMedicalError = notification?.error === "please provide medical-related conversation";
  const statusProps = getStatusProps(notification.status, isDarkTheme);

  const disabledRecording = ((isRecording || isPaused) && !isProgressReady) 



  const Timestamp = () => (
    <Typography fontSize={9} sx={{ fontWeight: "400", color: "text.secondary" }}>
      {formatNotificationTimestamp(notification.addedOn)}
    </Typography>
  );

  switch (notification.status) {
    case TASK_STATUS.COMPLETED:
      return (
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 1 }}>
          <Typography
            component="span"
            fontSize={12}
            sx={{
              fontWeight: "500",
              color: isDarkTheme ? "#a9a9ff" : "primary.main",
              ml: 4.5,
              opacity: (isDisabled ||disabledRecording|| isEncounterArchived) ? 0.5 : 1,
              cursor: (isDisabled ||disabledRecording|| isEncounterArchived || isUpdating) ? "not-allowed" : "pointer",
              pointerEvents: (isDisabled ||disabledRecording|| isEncounterArchived) ? "none" : "auto",
              "&:hover": (!isDisabled && !disabledRecording && !isEncounterArchived && !isUpdating) ? {
                background: statusProps.hoverColor
              } : {},
            }}
            onClick={(isDisabled || disabledRecording|| isEncounterArchived) ? () => { } : onClick}
          >
            {notification.task_category === "template"
              ? i18n.t("notifications.VIEW_TEMPLATE")
              : i18n.t("notifications.VIEW_ENCOUNTER")}
          </Typography>
          <Timestamp />
        </Box>
      );

    case TASK_STATUS.FAILED:
      return (
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 1 }}>
          <Box sx={{ flex: 1 }}>
            {notification?.error && (
              <Typography
                fontSize={12}
                sx={{
                  fontWeight: "500",
                  color: "#f44336",
                  mb: 0.5,
                  ml: 4.5,
                  opacity: (isDisabled || disabledRecording|| isEncounterArchived) ? 0.5 : 1,
                  cursor: (isDisabled || disabledRecording ||isEncounterArchived || isUpdating)
                    ? "not-allowed"
                    : "pointer",
                  pointerEvents: (isDisabled ||disabledRecording|| isEncounterArchived)
                    ? "none"
                    : "auto",
                }}
              >
                {notification.error}
              </Typography>
            )}

            {notification.task_category === "soap" && (
              <Box sx={{ display: "flex", alignItems: "center", ml: 4.5, gap: 1 }}>
                <Typography
                  component="span"
                  fontSize={12}
                  sx={{
                    fontWeight: 500,
                    color: isDarkTheme ? "#a9a9ff" : "primary.main",
                    opacity: (isDisabled || isEncounterArchived) ? 0.5 : 1,
                    cursor: (isDisabled || isEncounterArchived || isUpdating)
                      ? "not-allowed"
                      : "pointer",
                    pointerEvents: (isDisabled || isEncounterArchived)
                      ? "none"
                      : "auto",
                    "&:hover": (!isDisabled && !isEncounterArchived && !isUpdating) ? {
                      background: statusProps.hoverColor
                    } : {},
                  }}
                  onClick={(isDisabled || isEncounterArchived) ? () => { } : onClick}
                >
                  {soapNoteTask?.status === "COMPLETED"
                    ? i18n.t("notifications.VIEW_ENCOUNTER")
                    : i18n.t("notifications.VIEW_DRAFT_TRANSCRIPT")}
                </Typography>

                {soapNoteTask?.status === "FAILED" && !isNonMedicalError && !isDisabled && !isEncounterArchived && (
                  <Button
                    size="small"
                    startIcon={<RefreshIcon />}
                    onClick={(e) => {
                      e.stopPropagation();
                      onRetry(notification);
                    }}
                    sx={{
                      textTransform: "none",
                      fontSize: "12px",
                      fontWeight: 500,
                      minWidth: "auto",
                      color: isDarkTheme ? "#a9a9ff" : "primary.main",
                      "&:hover": {
                        backgroundColor: isDarkTheme ? "#5D5FEF14" : "#5D5FEF0A",
                      },
                    }}
                  >
                    {i18n.t("common.RETRY")}
                  </Button>
                )}
              </Box>
            )}
          </Box>
          <Timestamp />
        </Box>
      );

    default:
      return null;
  }
};

const ToastNotification = ({
  notification,
  onDelete,
  isDarkTheme,
  isDisabled,
  isUpdating,
  showReadNotifications,
}) => {
  const { i18n } = useTranslation();

  // 🔒 Disable toast in read mode
  const shouldBeDisabled = showReadNotifications || isDisabled;

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        padding: "8px 12px",
        margin: "2px 12px 8px",
        borderRadius: "8px",
        minHeight: "60px",
        background: isDarkTheme ? "#6163f121" : "#9e9ff11f",
        border: "1px solid #a2a7b17d",
        cursor: shouldBeDisabled ? "not-allowed" : "default",
      }}
    >
      {/* Content (disabled) */}
      <Typography
        fontSize={13}
        sx={{
          fontFamily: "Poppins",
          mb: "4px",
          flex: 1,
          opacity: shouldBeDisabled ? 0.5 : 1,
          pointerEvents: shouldBeDisabled ? "none" : "auto",
        }}
      >
        {notification?.message}
      </Typography>

      {/* Actions */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          ml: 1
        }}
      >
        <UITooltip title={i18n.t("common.DELETE")}>
          <span>
            <IconButton
              size="small"
              onClick={() => onDelete(notification)}
              disabled={isUpdating}
            >
              <ClearIcon fontSize="small" />
            </IconButton>
          </span>
        </UITooltip>

        <Typography
          fontSize={9}
          sx={{ fontWeight: "400", whiteSpace: "nowrap" }}
        >
          {formatNotificationTimestamp(notification?.addedOn)}
        </Typography>
      </Box>
    </Box>
  );
};

const BackgroundTaskNotification = ({
  notification,
  onDelete,
  isDarkTheme,
  handleNotificationClick,
  onRetry,
  onMarkAsUnread,
  showReadNotifications,
  isUpdating,
}) => {
  const { i18n } = useTranslation();
  const statusProps = getStatusProps(notification.status, isDarkTheme);
  const currentUserSettingsData = useSelector(
    (state) => state?.auth?.currentUserSettings
  );

  const soapNoteTask = notification?.taskGroups?.find(
    (item) => item.code === "task_group@notes.soapnote.generate"
  );

  const hasRetryOption =
    notification.status === TASK_STATUS.FAILED &&
    notification.task_category === "soap" &&
    soapNoteTask?.status === "FAILED";

  const isEncounterArchived =
    notification.task_category === "soap" &&
    !hasRetryOption &&
    notification?.encounter?.status === "entered-in-error";

  const isInProgress = [
    TASK_STATUS.IN_PROGRESS,
    TASK_STATUS.QUEUED,
    TASK_STATUS.RUNNING,
    TASK_STATUS.PENDING,
  ].includes(notification.status);

  // Determine if notification should be disabled
  // Disable if: showing read notifications AND this notification is read AND it's not in progress

      const patientObj = useSelector((state) => state?.patientObj);
    const encounterObj = useSelector((state) => state?.encounterObj);

    const { recording: isRecording, paused: isPaused } = useSelector((state) =>
      getCurrentRecordingState(state, patientObj?.patientObj?.id, encounterObj?.encounter?.id)
    );
    const activeGenerations = useSelector(
      (state) => state.activeSOAPGeneration?.activeGenerations
    );
  
    const progressState = useSelector((state) =>
      getCurrentProgressState(state, patientObj?.patientObj?.id, encounterObj?.encounter?.id, activeGenerations)
    );
  
    const { isProgressReady } = progressState || {};

  const isDisabled = showReadNotifications && notification.isRead && !isInProgress  ; 
  // Determine if notification is clickable
  const isClickable =
    !isDisabled &&
    !isInProgress && !isEncounterArchived &&
    (notification.status === TASK_STATUS.COMPLETED ||
      (notification.status === TASK_STATUS.FAILED &&
        notification.task_category === "soap"));

  const isNonMedicalError = notification?.error === "please provide medical-related conversation";

  const text = i18n.t("Patient_Screen.CREATED_SUCCESSFULLY", {
    lng: LANGUAGE_CODE_MAP[currentUserSettingsData?.display?.clinicalNoteLanguage],
  });

  const ADD_CREATED_SUCCESSFULLY =
    notification?.task_category === "soap" && notification?.status === TASK_STATUS.COMPLETED;

  return (
    <>
      <Box
        sx={{
          padding: "12px",
          margin: "4px 12px 6px",
          borderRadius: "8px",
          background: statusProps.color,
          border: `0.5px solid ${statusProps.borderColor}`,
          transition: "background 0.2s ease-in-out, opacity 0.2s ease-in-out",
          ...(isEncounterArchived && {
            border: `0.5px solid rgb(255, 193, 7)`,
            opacity: 0.7,
            filter: "grayscale(40%)",
          }),
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 1 }}>
          <Box sx={{ display: "flex", flex: 1, gap: 1, opacity: isDisabled ? 0.5 : 1 }}>
            {statusProps.icon}

            <Typography fontSize={13} sx={{ flex: 1 }}>
              {isInProgress && notification.patientName ? (
                <>
                  {isNonMedicalError ? (
                    i18n.t("common.NON_MEDICAL_CONVERSATION")
                  ) : (
                    <>
                      {notification?.title}
                      {ADD_CREATED_SUCCESSFULLY ? ` ${text}` : ""} for{" "}
                      <Box component="span" sx={{ fontWeight: 600, display: "inline" }}>
                        {notification.patientName}
                      </Box>
                    </>
                  )}
                </>
              ) : notification.patientName ? (
                <>
                  {isEncounterArchived && (
                    <Typography fontSize={12} sx={{ fontWeight: 600, color: "rgb(255, 193, 7)", mb: 0.5 }}>
                      {i18n.t("notifications.ENCOUNTER_ARCHIVED")}
                    </Typography>
                  )}

                  <div style={{ color: isDarkTheme ? "#A0A4AB" : "#757677ff" }}>
                    {i18n.t("Patient_Screen.PATIENT_NAME")} :{" "}
                    <Box component="span" sx={{ fontWeight: 600, display: "inline" }}>
                      {notification?.patientName || "-"}
                    </Box>
                  </div>

                  <div>
                    {isNonMedicalError
                      ? i18n.t("common.NON_MEDICAL_CONVERSATION")
                      : `${i18n.t("common.SOAP_NOTE_FOR", { lng: LANGUAGE_CODE_MAP[currentUserSettingsData?.display?.clinicalNoteLanguage] })} ${notification?.title}${ADD_CREATED_SUCCESSFULLY ? ` ${text}` : ""}`}
                  </div>
                </>
              ) : isNonMedicalError ? (
                i18n.t("common.NON_MEDICAL_CONVERSATION")
              ) : (
                `${notification?.title}${ADD_CREATED_SUCCESSFULLY ? ` ${text}` : ""}`
              )}
            </Typography>
          </Box>

          {!isInProgress && (
            <Box
              sx={{
                display: "flex",
                gap: 0.5,
              }}
            >
              {showReadNotifications && (
                <UITooltip title={i18n.t("notifications.MARK_AS_UNREAD")}>
                  <span>
                    <Button
                      size="small"
                      color="primary"
                      disabled={isUpdating || isEncounterArchived}
                      sx={{
                        mt: "-4px",
                        fontSize: "12px",
                        textTransform: "none",
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        onMarkAsUnread(notification);
                      }}
                    >
                      {i18n.t("notifications.UNREAD")}
                    </Button>
                  </span>
                </UITooltip>
              )}

              <UITooltip title={i18n.t("common.DELETE")}>
                <span>
                  <IconButton
                    size="small"
                    disabled={isUpdating}
                    onClick={(e) => {
                      e.stopPropagation();
                      onDelete(notification);
                    }}
                    sx={{ mt: "-4px" }}
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </span>
              </UITooltip>
            </Box>
          )}
        </Box>

        {isInProgress && (
          <Box sx={{ mt: 1 }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
              <ProgressBar progress={notification.progress} customSx={{ flex: 1 }} showSteps={false} />
              <Chip
                label={`${notification.progress}%`}
                size="small"
                sx={{ fontSize: "12px", height: "20px", background: "transparent", border: "none" }}
              />
            </Box>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: "4px" }}>
              <UITooltip title={typeof notification.message === "string" ? notification.message : ""}>
                <Typography
                  fontSize={11}
                  sx={{
                    fontFamily: "Poppins",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    pr: 1,
                  }}
                >
                  {notification.message}
                </Typography>
              </UITooltip>
              <Typography fontSize={9} sx={{ fontWeight: "400", flexShrink: 0 }}>
                {formatNotificationTimestamp(notification?.addedOn)}
              </Typography>
            </Box>
          </Box>
        )}

        <BackgroundTaskFooter
          notification={notification}
          isDarkTheme={isDarkTheme}
          i18n={i18n}
          onRetry={onRetry}
          isDisabled={isDisabled}
          isClickable={isClickable}
          onClick={() => isClickable && !isUpdating && handleNotificationClick(notification)}
          isUpdating={isUpdating}
          isInProgress={isInProgress}
          isEncounterArchived={isEncounterArchived}
          isRecording={isRecording}
          isPaused={isPaused}
          isProgressReady={isProgressReady}
        />
      </Box>
      <Divider variant="middle" />
    </>
  );
};

const NotificationItem = (props) => {
  const { notification } = props;

  if (notification.type !== "background_task") {
    return <ToastNotification {...props} />;
  }

  return <BackgroundTaskNotification {...props} />;
};

export default NotificationItem;